import type { LevelConfig } from '../types/content';

const chars = ['rebel', 'jugaadu', 'gen-z'] as const;

function stage(
  partial: Omit<LevelConfig, 'cityId' | 'missionId' | 'chapterOrder' | 'version' | 'allowedCharacterIds' | 'musicKey'> & {
    musicKey?: string;
  },
): LevelConfig {
  return {
    cityId: 'fictional-delhi',
    missionId: 'recover-the-mic',
    chapterOrder: 1,
    version: 4,
    allowedCharacterIds: [...chars],
    musicKey: partial.musicKey ?? 'delhi-street-loop',
    ...partial,
  };
}

function paperStage(
  partial: Omit<LevelConfig, 'cityId' | 'missionId' | 'chapterOrder' | 'version' | 'allowedCharacterIds' | 'musicKey'> & {
    musicKey?: string;
  },
): LevelConfig {
  return {
    cityId: 'fictional-delhi',
    missionId: 'seal-the-paper-trail',
    chapterOrder: 2,
    version: 4,
    allowedCharacterIds: [...chars],
    musicKey: partial.musicKey ?? 'press-room-loop',
    ...partial,
  };
}

export const mission1Stages: LevelConfig[] = [
  stage({
    id: 'mic-01-chai-drill',
    name: 'Chai Drill',
    shortDescription: 'Learn the gali. Jump, crouch, grab a token.',
    stageOrder: 1,
    mapId: 'mic-01-chai-drill',
    bossStage: false,
    backgroundKey: 'delhi-jantar-wide-v3',
    checkpointIds: ['cp-mid'],
    enemyDensity: { min: 3, max: 6 },
    mission: {
      id: 'chai-drill',
      title: 'Warm up at the cart',
      steps: [
        { id: 'find-mic', type: 'collect', targetId: 'peoples-mic', instruction: 'Collect the practice mic tag' },
        { id: 'power-stage', type: 'deliver', targetId: 'temporary-stage', instruction: 'Tap the practice stage' },
        { id: 'escape', type: 'escape', targetId: 'metro-exit', instruction: 'Reach the end of the block' },
      ],
    },
  }),
  stage({
    id: 'mic-02-barricade-bazaar',
    name: 'Barricade Bazaar',
    shortDescription: 'First patrol clusters and cover tarps.',
    stageOrder: 2,
    mapId: 'mic-02-barricade-bazaar',
    bossStage: false,
    backgroundKey: 'delhi-jantar-wide-v3',
    checkpointIds: ['cp-mid'],
    enemyDensity: { min: 3, max: 5 },
    mission: {
      id: 'barricade-bazaar',
      title: 'Slip the bazaar cordon',
      steps: [
        { id: 'find-mic', type: 'collect', targetId: 'peoples-mic', instruction: 'Recover the bazaar tip-off' },
        { id: 'power-stage', type: 'deliver', targetId: 'temporary-stage', instruction: 'Stamp it at the stall stage' },
        { id: 'escape', type: 'escape', targetId: 'metro-exit', instruction: 'Exit past the cordon' },
      ],
    },
  }),
  stage({
    id: 'mic-03-news-van-crossing',
    name: 'News Van Crossing',
    shortDescription: 'Drones overhead. Rebel-high roofs for bonus tokens.',
    stageOrder: 3,
    mapId: 'mic-03-news-van-crossing',
    bossStage: false,
    backgroundKey: 'delhi-jantar-wide-v3',
    checkpointIds: ['cp-mid'],
    enemyDensity: { min: 5, max: 7 },
    mission: {
      id: 'news-van-crossing',
      title: 'Cross while cameras spin',
      steps: [
        { id: 'find-mic', type: 'collect', targetId: 'peoples-mic', instruction: 'Snag the confiscated clip' },
        { id: 'power-stage', type: 'deliver', targetId: 'temporary-stage', instruction: 'Feed the temporary desk' },
        { id: 'escape', type: 'escape', targetId: 'metro-exit', instruction: 'Clear the crossing' },
      ],
    },
  }),
  stage({
    id: 'mic-04-confiscation-lane',
    name: 'Confiscation Lane',
    shortDescription: 'Mic pickup. Crawl under the kiosk for a shortcut.',
    stageOrder: 4,
    mapId: 'mic-04-confiscation-lane',
    bossStage: false,
    backgroundKey: 'delhi-jantar-wide-v3',
    undergroundBackgroundKey: 'delhi-metro-underground-v3',
    checkpointIds: ['cp-mic', 'cp-mid'],
    enemyDensity: { min: 5, max: 7 },
    mission: {
      id: 'confiscation-lane',
      title: "Lift the People's Mic",
      steps: [
        { id: 'find-mic', type: 'collect', targetId: 'peoples-mic', instruction: 'Find the confiscation crate' },
        { id: 'carry-mic', type: 'deliver', targetId: 'temporary-stage', instruction: 'Carry it past the lane' },
        { id: 'escape', type: 'escape', targetId: 'metro-exit', instruction: 'Exit the lane' },
      ],
    },
  }),
  stage({
    id: 'mic-05-barricade-captain',
    name: 'Barricade Captain',
    shortDescription: 'Mid-boss: fictional Barricade Captain arena.',
    stageOrder: 5,
    mapId: 'mic-05-barricade-captain',
    bossStage: true,
    backgroundKey: 'delhi-jantar-wide-v3',
    checkpointIds: ['cp-arena'],
    enemyDensity: { min: 1, max: 2 },
    mission: {
      id: 'barricade-captain',
      title: 'Outlast the cordon boss',
      steps: [
        { id: 'boss-fight', type: 'boss', targetId: 'boss-arena', instruction: 'Wait for TIRED, then bump the Barricade Captain' },
        { id: 'escape', type: 'escape', targetId: 'metro-exit', instruction: 'Leave the arena' },
      ],
    },
  }),
  stage({
    id: 'mic-06-gas-gali',
    name: 'Gas Gali',
    shortDescription: 'Tear-gas denser. Time the clouds.',
    stageOrder: 6,
    mapId: 'mic-06-gas-gali',
    bossStage: false,
    backgroundKey: 'delhi-connaught-wide-v3',
    checkpointIds: ['cp-mid'],
    enemyDensity: { min: 5, max: 7 },
    mission: {
      id: 'gas-gali',
      title: 'Hold breath, keep moving',
      steps: [
        { id: 'find-mic', type: 'collect', targetId: 'peoples-mic', instruction: 'Recover the relay packet' },
        { id: 'power-stage', type: 'deliver', targetId: 'temporary-stage', instruction: 'Drop it at the gali desk' },
        { id: 'escape', type: 'escape', targetId: 'metro-exit', instruction: 'Clear Gas Gali' },
      ],
    },
  }),
  stage({
    id: 'mic-07-wire-tangle',
    name: 'Wire Tangle',
    shortDescription: 'Moving lifts and Jugaadu crawl pressure.',
    stageOrder: 7,
    mapId: 'mic-07-wire-tangle',
    bossStage: false,
    backgroundKey: 'delhi-connaught-wide-v3',
    undergroundBackgroundKey: 'delhi-metro-underground-v3',
    checkpointIds: ['cp-mid'],
    enemyDensity: { min: 5, max: 7 },
    mission: {
      id: 'wire-tangle',
      title: 'Fix what you must, crawl what you can',
      steps: [
        { id: 'find-mic', type: 'collect', targetId: 'peoples-mic', instruction: 'Grab the tangled feed' },
        { id: 'power-stage', type: 'activate', targetId: 'temporary-stage', instruction: 'Power the junction box' },
        { id: 'escape', type: 'escape', targetId: 'metro-exit', instruction: 'Ride the last lift out' },
      ],
    },
  }),
  stage({
    id: 'mic-08-stage-power',
    name: 'Stage Power',
    shortDescription: 'Dense Gen Z streak lane, then van chase on escape.',
    stageOrder: 8,
    mapId: 'mic-08-stage-power',
    bossStage: false,
    backgroundKey: 'delhi-connaught-wide-v3',
    checkpointIds: ['cp-mid'],
    enemyDensity: { min: 5, max: 7 },
    mission: {
      id: 'stage-power',
      title: 'Power the temporary stage',
      steps: [
        { id: 'find-mic', type: 'collect', targetId: 'peoples-mic', instruction: 'Collect the stage key' },
        { id: 'power-stage', type: 'deliver', targetId: 'temporary-stage', instruction: 'Power the temporary stage' },
        { id: 'escape', type: 'escape', targetId: 'metro-exit', instruction: 'Exit stage left' },
      ],
    },
  }),
  stage({
    id: 'mic-09-metro-approach',
    name: 'Metro Approach',
    shortDescription: 'Chase prep. All three bonus routes open.',
    stageOrder: 9,
    mapId: 'mic-09-metro-approach',
    bossStage: false,
    backgroundKey: 'delhi-jantar-wide-v3',
    undergroundBackgroundKey: 'delhi-metro-underground-v3',
    checkpointIds: ['cp-mid'],
    enemyDensity: { min: 8, max: 10 },
    mission: {
      id: 'metro-approach',
      title: 'Approach the elevated exit',
      steps: [
        { id: 'find-mic', type: 'collect', targetId: 'peoples-mic', instruction: 'Secure the last tip' },
        { id: 'power-stage', type: 'deliver', targetId: 'temporary-stage', instruction: 'Brief the stage crew' },
        { id: 'escape', type: 'escape', targetId: 'metro-exit', instruction: 'Reach the Metro-inspired exit' },
      ],
    },
  }),
  stage({
    id: 'mic-10-detention-superintendent',
    name: 'Detention Superintendent',
    shortDescription: 'Final boss, then bhaago.',
    stageOrder: 10,
    mapId: 'mic-10-detention-superintendent',
    bossStage: true,
    backgroundKey: 'delhi-jantar-wide-v3',
    checkpointIds: ['cp-arena'],
    enemyDensity: { min: 1, max: 2 },
    musicKey: 'press-room-loop',
    mission: {
      id: 'detention-superintendent',
      title: 'Beat the van memo, then escape',
      steps: [
        { id: 'boss-fight', type: 'boss', targetId: 'boss-arena', instruction: 'Wait for TIRED, then bump the Detention Superintendent' },
        { id: 'escape', type: 'escape', targetId: 'metro-exit', instruction: 'Bhaago to the exit' },
      ],
    },
  }),
];

export const mission2Stages: LevelConfig[] = [
  paperStage({
    id: 'paper-01-printing-lane',
    name: 'Jantar File Queue',
    shortDescription: 'Return to Jantar Mantar with verification pressure.',
    stageOrder: 1,
    mapId: 'paper-01-printing-lane',
    bossStage: false,
    backgroundKey: 'delhi-jantar-wide-v3',
    checkpointIds: ['cp-mid'],
    enemyDensity: { min: 3, max: 5 },
    mission: {
      id: 'jantar-file-queue',
      title: 'Secure the loose file seal',
      steps: [
        { id: 'find-mic', type: 'collect', targetId: 'peoples-mic', instruction: 'Collect the loose file seal' },
        { id: 'power-stage', type: 'deliver', targetId: 'temporary-stage', instruction: 'Drop it at the civic desk' },
        { id: 'escape', type: 'escape', targetId: 'metro-exit', instruction: 'Exit the Jantar block' },
      ],
    },
  }),
  paperStage({
    id: 'paper-02-stamp-market',
    name: 'Permit Stamp Row',
    shortDescription: 'Shield blockers and Gen Z streak windows near the railings.',
    stageOrder: 2,
    mapId: 'paper-02-stamp-market',
    bossStage: false,
    backgroundKey: 'delhi-jantar-wide-v3',
    checkpointIds: ['cp-mid'],
    enemyDensity: { min: 4, max: 6 },
    mission: {
      id: 'permit-stamp-row',
      title: 'Sort the permit chaos',
      steps: [
        { id: 'find-mic', type: 'collect', targetId: 'peoples-mic', instruction: 'Find the clean permit tag' },
        { id: 'power-stage', type: 'deliver', targetId: 'temporary-stage', instruction: 'Mark it at the civic desk' },
        { id: 'escape', type: 'escape', targetId: 'metro-exit', instruction: 'Leave the permit row' },
      ],
    },
  }),
  paperStage({
    id: 'paper-03-gas-ledger',
    name: 'Smoke Register',
    shortDescription: 'Tear-gas units control the street.',
    stageOrder: 3,
    mapId: 'paper-03-gas-ledger',
    bossStage: false,
    backgroundKey: 'delhi-jantar-wide-v3',
    checkpointIds: ['cp-mid'],
    enemyDensity: { min: 5, max: 7 },
    mission: {
      id: 'smoke-register',
      title: 'Cross the smoke register',
      steps: [
        { id: 'find-mic', type: 'collect', targetId: 'peoples-mic', instruction: 'Recover the queue chit' },
        { id: 'power-stage', type: 'deliver', targetId: 'temporary-stage', instruction: 'File it at the civic desk' },
        { id: 'escape', type: 'escape', targetId: 'metro-exit', instruction: 'Escape before the next gas arc' },
      ],
    },
  }),
  paperStage({
    id: 'paper-04-basement-bundles',
    name: 'Metro Slip Vault',
    shortDescription: 'Jugaadu crawl route under the Metro-inspired concourse.',
    stageOrder: 4,
    mapId: 'paper-04-basement-bundles',
    bossStage: false,
    backgroundKey: 'delhi-jantar-wide-v3',
    undergroundBackgroundKey: 'delhi-metro-underground-v3',
    checkpointIds: ['cp-mid'],
    enemyDensity: { min: 5, max: 7 },
    mission: {
      id: 'metro-slip-vault',
      title: 'Duck under the concourse maze',
      steps: [
        { id: 'find-mic', type: 'collect', targetId: 'peoples-mic', instruction: 'Grab the underpass access tag' },
        { id: 'power-stage', type: 'deliver', targetId: 'temporary-stage', instruction: 'Bring it to the desk' },
        { id: 'escape', type: 'escape', targetId: 'metro-exit', instruction: 'Exit above ground' },
      ],
    },
  }),
  paperStage({
    id: 'paper-05-packet-auditor',
    name: 'Packet Auditor',
    shortDescription: 'Mid-boss with slam shock lines.',
    stageOrder: 5,
    mapId: 'paper-05-packet-auditor',
    bossStage: true,
    backgroundKey: 'delhi-jantar-wide-v3',
    checkpointIds: ['cp-arena'],
    enemyDensity: { min: 1, max: 2 },
    mission: {
      id: 'packet-auditor',
      title: 'Outlast the Packet Auditor',
      steps: [
        { id: 'boss-fight', type: 'boss', targetId: 'boss-arena', instruction: 'Jump the slam, then bump during TIRED' },
        { id: 'escape', type: 'escape', targetId: 'metro-exit', instruction: 'Exit the Jantar cordon' },
      ],
    },
  }),
  paperStage({
    id: 'paper-06-carbon-copy',
    name: 'CP Carbon Copy',
    shortDescription: 'Rumour bots and scanners chain pressure.',
    stageOrder: 6,
    mapId: 'paper-06-carbon-copy',
    bossStage: false,
    backgroundKey: 'delhi-connaught-wide-v3',
    checkpointIds: ['cp-mid'],
    enemyDensity: { min: 6, max: 8 },
    mission: {
      id: 'cp-carbon-copy',
      title: 'Break the duplicate trail at CP',
      steps: [
        { id: 'find-mic', type: 'collect', targetId: 'peoples-mic', instruction: 'Find the duplicate route slip' },
        { id: 'power-stage', type: 'deliver', targetId: 'temporary-stage', instruction: 'Sort it at the civic desk' },
        { id: 'escape', type: 'escape', targetId: 'metro-exit', instruction: 'Leave before copies multiply' },
      ],
    },
  }),
  paperStage({
    id: 'paper-07-wire-room',
    name: 'Colonnade Wire Room',
    shortDescription: 'Moving lifts, crawl bonus, and enemy pressure.',
    stageOrder: 7,
    mapId: 'paper-07-wire-room',
    bossStage: false,
    backgroundKey: 'delhi-connaught-wide-v3',
    undergroundBackgroundKey: 'delhi-metro-underground-v3',
    checkpointIds: ['cp-mid'],
    enemyDensity: { min: 6, max: 8 },
    mission: {
      id: 'colonnade-wire-room',
      title: 'Patch the colonnade wire',
      steps: [
        { id: 'find-mic', type: 'collect', targetId: 'peoples-mic', instruction: 'Grab the wire-room pass' },
        { id: 'power-stage', type: 'activate', targetId: 'temporary-stage', instruction: 'Power the civic desk' },
        { id: 'escape', type: 'escape', targetId: 'metro-exit', instruction: 'Ride out of the wire room' },
      ],
    },
  }),
  paperStage({
    id: 'paper-08-gas-stampede',
    name: 'CP Gas Stampede',
    shortDescription: 'Gas arcs plus a short chase.',
    stageOrder: 8,
    mapId: 'paper-08-gas-stampede',
    bossStage: false,
    backgroundKey: 'delhi-connaught-wide-v3',
    checkpointIds: ['cp-mid'],
    enemyDensity: { min: 7, max: 9 },
    mission: {
      id: 'cp-gas-stampede',
      title: 'Sprint through the colonnade smoke',
      steps: [
        { id: 'find-mic', type: 'collect', targetId: 'peoples-mic', instruction: 'Collect the final desk tab' },
        { id: 'power-stage', type: 'deliver', targetId: 'temporary-stage', instruction: 'Lock it at the desk' },
        { id: 'escape', type: 'escape', targetId: 'metro-exit', instruction: 'Run past the gas line' },
      ],
    },
  }),
  paperStage({
    id: 'paper-09-archive-approach',
    name: 'Archive Approach',
    shortDescription: 'All three bonus routes before the finale.',
    stageOrder: 9,
    mapId: 'paper-09-archive-approach',
    bossStage: false,
    backgroundKey: 'delhi-connaught-wide-v3',
    undergroundBackgroundKey: 'delhi-metro-underground-v3',
    checkpointIds: ['cp-mid'],
    enemyDensity: { min: 8, max: 10 },
    mission: {
      id: 'cp-archive-approach',
      title: 'Approach the CP archive desk',
      steps: [
        { id: 'find-mic', type: 'collect', targetId: 'peoples-mic', instruction: 'Secure the archive pass' },
        { id: 'power-stage', type: 'deliver', targetId: 'temporary-stage', instruction: 'Brief the archive desk' },
        { id: 'escape', type: 'escape', targetId: 'metro-exit', instruction: 'Reach the archive exit' },
      ],
    },
  }),
  paperStage({
    id: 'paper-10-verification-chief',
    name: 'Verification Chief',
    shortDescription: 'Final boss mixes gas, charges, and TIRED windows.',
    stageOrder: 10,
    mapId: 'paper-10-verification-chief',
    bossStage: true,
    backgroundKey: 'delhi-connaught-wide-v3',
    checkpointIds: ['cp-arena'],
    enemyDensity: { min: 1, max: 2 },
    mission: {
      id: 'verification-chief',
      title: 'Seal the Paper Trail',
      steps: [
        { id: 'boss-fight', type: 'boss', targetId: 'boss-arena', instruction: 'Dodge gas calls, then bump during TIRED' },
        { id: 'escape', type: 'escape', targetId: 'metro-exit', instruction: 'Escape with the seal intact' },
      ],
    },
  }),
];

export const levels = [...mission1Stages, ...mission2Stages];

export const getLevel = (id: string) => levels.find((level) => level.id === id) ?? levels[0];

export function isStageUnlocked(levelId: string, completedLevels: string[]): boolean {
  const level = getLevel(levelId);
  if (level.stageOrder <= 1) return true;
  const previous = levels.find((entry) => entry.missionId === level.missionId && entry.stageOrder === level.stageOrder - 1);
  return Boolean(previous && completedLevels.includes(previous.id));
}
